"use client";

import { Dialog, DialogContent } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { WalletSetupSkeletonView } from "./views/wallet-setup-skeleton-view";

// Load the flow (and the Turnkey SDK it pulls in) only when the dialog first opens; ssr:false because
// useTurnkey() needs the client-only provider. The loading fallback keeps a DialogTitle mounted so
// Radix's labelling contract holds during the async import, and matches the coordinator's loading skeleton.
const CreateEmbeddedWalletFlow = dynamic(() => import("./create-embedded-wallet-flow"), {
  loading: () => <WalletSetupSkeletonView />,
  ssr: false,
});

export function CreateEmbeddedWalletDialog({ dialogControls }: { dialogControls: UseDialogReturn<void> }) {
  const enabled = useCanCreateEmbeddedWallet();
  // The flow locks the modal while the wallet is persisting (the "creating" step) so a close can't
  // interrupt the in-flight save.
  const [locked, setLocked] = useState(false);
  // Gate entry on readiness, but never unmount an open modal: `enabled` includes a live PostHog flag whose
  // transient non-true read on tab refocus would otherwise tear down an in-flight OTP flow.
  if (!enabled && !dialogControls.props.open) return null;

  // Radix unmounts DialogContent on close, so the flow's step/email/otp state resets each open for free.
  return (
    <Dialog
      onOpenChange={(open) => {
        // Block programmatic/X close while locked; escape + outside-click are blocked on every step below.
        if (!open && locked) return;
        dialogControls.props.onOpenChange(open);
      }}
      open={dialogControls.props.open}
    >
      <DialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <CreateEmbeddedWalletFlow onClose={dialogControls.dismiss} onLockedChange={setLocked} />
      </DialogContent>
    </Dialog>
  );
}
